<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import type { FieldSchema } from '../../../types';
import type { MCartSplitPaymentPayload, StripeCardModel } from '@ShoppingCartModule';
import { formatCurrency, toCents } from '@Helpers';
import { useSessionStore } from '@Stores';
import { useForm } from '@FormModule';

const props = withDefaults(
  defineProps<{
    field: FieldSchema;
    disableCreditCard?: boolean;
    editMode?: boolean;
    error?: boolean | string;
  }>(),
  {
    disableCreditCard: false,
    editMode: false,
  }
);

const { shelterProfile } = storeToRefs(useSessionStore());

const { wasFormSubmitted } = useForm();

const model = defineModel<MCartSplitPaymentPayload>();

const cardReaderAmount = ref<number>(0);
const cardReaderLastFour = ref<string>('');
const cashAmount = ref<number>(0);
const checkAmount = ref<number>(0);
const checkNumber = ref<string>('');
const creditCardAmount = ref<number | null>(null);
const otherAmount = ref<number>(0);
const creditCardModel = ref<StripeCardModel>({
  cardComplete: false,
  cardElement: null,
  cardHolderName: '',
  stripe: null,
  stripeError: '',
});

const creditCardEnabled = computed(() => props.field.settings?.creditCardEnabled ?? true);
const cartTotal = computed(() => Number(props.field.settings?.cartTotal ?? 0));
const cartTotalCents = computed(() => Math.round(cartTotal.value * 100));
const remainingAmount = computed(() => (cartTotalCents.value - splitTotalCents.value) / 100);
const showCreditCardFee = computed(
  () =>
    shelterProfile.value?.charge_adopter_cc_fee &&
    (creditCardAmount.value ?? 0) > 0 &&
    Number(props.field.settings.cc_fee) > 0
);
const splitTotal = computed(() => splitTotalCents.value / 100);
const _model = computed(() => ({
  card_reader_amount: cardReaderAmount.value,
  cart_total: cartTotal.value,
  cash_amount: cashAmount.value,
  check_amount: checkAmount.value,
  check_number: checkNumber.value,
  credit_card_amount: creditCardEnabled.value ? creditCardAmount.value : 0,
  last_four_digits: cardReaderLastFour.value,
  name_on_card: creditCardModel.value.cardHolderName,
  other_amount: otherAmount.value,
  split_total: splitTotal.value,
  card_complete: creditCardModel.value.cardComplete,
  stripe_card: creditCardModel.value, // NOTE :: live Stripe objects for the confirm step. Never send them to the api.
}));
const splitTotalCents = computed(() => {
  let total =
    toCents(cashAmount.value) +
    toCents(checkAmount.value) +
    toCents(otherAmount.value) +
    toCents(cardReaderAmount.value);

  if (creditCardEnabled.value) total += toCents(creditCardAmount.value);

  return total;
});
const splitTotalBgColor = computed(() =>
  // prettier-ignore
  splitTotalCents.value === cartTotalCents.value ? 'bg-[#4fb2ad]'
    : splitTotalCents.value > cartTotalCents.value ? 'bg-[#ef4444]'
    : 'bg-[#f97316]'
);

watch(_model, () => (model.value = _model.value));
// NOTE :: The card field inside this split has no `field` prop, so it cannot mark the form itself.
// Copy its Stripe error (a typo, or a decline from the confirm step) onto this field's errors.
// That blocks submit, and it lets the form show "review the fields above".
watch(
  () => creditCardModel.value.stripeError,
  (stripeError) => onStripeCardErrorUpdate(stripeError)
);

onMounted(() => initModel());

// EVENTS
function onStripeCardErrorUpdate(stripeError: string) {
  const STRIPE_ERROR_KEY = 'components.forms.errors.stripeValidationError';

  if (stripeError) {
    props.field.state.errors.add(STRIPE_ERROR_KEY);
    return;
  }

  props.field.state.errors.delete(STRIPE_ERROR_KEY);
}

// HELPERS
function initModel() {
  wasFormSubmitted.value = false;

  if (!model.value) return;

  cardReaderAmount.value = model.value.card_reader_amount != null ? Number(model.value.card_reader_amount) : 0;
  cardReaderLastFour.value = model.value.last_four_digits != null ? String(model.value.last_four_digits) : '';
  cashAmount.value = model.value.cash_amount != null ? Number(model.value.cash_amount) : 0;
  checkAmount.value = model.value.check_amount != null ? Number(model.value.check_amount) : 0;
  checkNumber.value = model.value.check_number != null ? String(model.value.check_number) : '';
  creditCardAmount.value =
    creditCardEnabled.value && model.value.credit_card_amount != null ? Number(model.value.credit_card_amount) : 0;
  otherAmount.value = model.value.other_amount != null ? Number(model.value.other_amount) : 0;
  creditCardModel.value = {
    cardComplete: false,
    cardElement: null,
    cardHolderName: model.value.name_on_card ?? '',
    stripe: null,
    stripeError: '',
  };
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <Divider
      class="my-0 before:border-t-2"
      type="dashed"
    />

    <div class="sticky top-0 z-10 flex max-w-[500px] flex-col gap-2 bg-white">
      <div class="flex flex-col items-start gap-2">
        <div class="flex items-center gap-2">
          <FaIcon :icon="$icons.faMoneyCheckDollar" />

          <h2 :class="['text-lg font-semibold', error && 'text-red-500']">
            {{ $t('cart.splitPayment.title') }}
          </h2>
        </div>

        <div class="flex items-center gap-2">
          <span :class="['rounded-md px-2 py-1 text-sm text-white', splitTotalBgColor]">
            {{ $t('cart.splitPayment.total') }} {{ `$${formatCurrency(splitTotal)} / $${formatCurrency(cartTotal)}` }}
          </span>

          <div class="flex items-center gap-2">
            <FaIcon
              :class="error && 'text-red-500'"
              :icon="$icons.faInfoCircle"
            />

            <p :class="['text-sm text-gray-500', error && 'text-red-500']">
              ${{ formatCurrency(remainingAmount) }} {{ $t('cart.splitPayment.remaining') }}
            </p>
          </div>
        </div>
      </div>

      <p :class="['text-sm text-gray-500', error && 'text-red-500']">
        {{ $t('cart.splitPayment.description') }}
      </p>
    </div>

    <div class="flex max-w-[500px] flex-col gap-4">
      <div>
        <AFieldLabel
          :class="cashAmount > 0 && error && 'text-red-500'"
          :label="$t('cart.splitPayment.cashAmount')"
          :required="cashAmount > 0"
        />

        <AFieldMoney
          v-model="cashAmount"
          resetZeroModelOnFocus
          :error="cashAmount > 0 && error"
          :required="cashAmount > 0"
        />
      </div>

      <div
        v-if="creditCardEnabled"
        class="flex flex-col gap-4"
      >
        <AFieldSeparator
          align="left"
          content="cart.splitPayment.creditCard"
          type="dashed"
        />

        <div>
          <AFieldLabel
            :class="(creditCardAmount ?? 0) > 0 && error && 'text-red-500'"
            :label="$t('cart.splitPayment.creditCardAmount')"
            :required="(creditCardAmount ?? 0) > 0"
          />

          <AFieldMoney
            v-model="creditCardAmount"
            resetZeroModelOnFocus
            :disabled="disableCreditCard"
            :error="(creditCardAmount ?? 0) > 0 && error"
            :required="(creditCardAmount ?? 0) > 0"
          />

          <p
            v-if="showCreditCardFee"
            class="text-sm text-gray-500"
          >
            {{ $t('cart.splitPayment.creditCardFee') }} ${{ field.settings?.cc_fee }}
          </p>
        </div>

        <MFieldStripeCreditCard
          v-if="!editMode"
          v-model="creditCardModel"
          :creditCardNameRequired="(creditCardAmount ?? 0) > 0"
          :creditCardNumberRequired="(creditCardAmount ?? 0) > 0"
          :error="(creditCardAmount ?? 0) > 0 && error"
          :required="(creditCardAmount ?? 0) > 0"
        />
      </div>

      <div class="flex flex-col gap-4">
        <AFieldSeparator
          align="left"
          content="cart.splitPayment.check"
          type="dashed"
        />

        <div>
          <AFieldLabel
            :class="checkAmount > 0 && error && 'text-red-500'"
            :label="$t('cart.splitPayment.checkAmount')"
            :required="checkAmount > 0"
          />

          <AFieldMoney
            v-model="checkAmount"
            resetZeroModelOnFocus
            :error="checkAmount > 0 && error"
            :required="checkAmount > 0"
          />
        </div>

        <div>
          <AFieldLabel
            :class="checkAmount > 0 && error && 'text-red-500'"
            :label="$t('cart.splitPayment.checkNumber')"
            :required="false"
          />

          <AFieldCheckNumber
            v-model="checkNumber"
            :error="checkAmount > 0 && error"
            :required="false"
          />
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <AFieldSeparator
          align="left"
          content="cart.splitPayment.cardReader"
          type="dashed"
        />

        <div>
          <AFieldLabel
            :class="cardReaderAmount > 0 && error && 'text-red-500'"
            :label="$t('cart.splitPayment.cardReaderAmount')"
            :required="cardReaderAmount > 0"
          />

          <AFieldMoney
            v-model="cardReaderAmount"
            resetZeroModelOnFocus
            :error="cardReaderAmount > 0 && error"
            :required="cardReaderAmount > 0"
          />
        </div>

        <div>
          <AFieldLabel
            :class="cardReaderAmount > 0 && error && 'text-red-500'"
            :label="$t('cart.label.lastFour')"
            :required="false"
          />

          <AFieldCardReader
            v-model="cardReaderLastFour"
            :error="cardReaderAmount > 0 && error"
            :required="false"
          />
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <AFieldSeparator
          align="left"
          content="cart.splitPayment.other"
          type="dashed"
        />

        <div>
          <AFieldLabel
            :class="otherAmount > 0 && error && 'text-red-500'"
            :label="$t('cart.splitPayment.otherAmount')"
            :required="otherAmount > 0"
          />

          <AFieldMoney
            v-model="otherAmount"
            resetZeroModelOnFocus
            :error="otherAmount > 0 && error"
            :required="otherAmount > 0"
          />
        </div>
      </div>
    </div>

    <Divider
      class="mb-0 mt-2 before:border-t-2"
      type="dashed"
    />
  </div>
</template>
